import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MerchantPayoutModal } from '../components/payouts/MerchantPayoutModal';
import { copyText } from '../components/payouts/payoutUtils';
import { 
  Send, 
  Plus, 
  Search, 
  Filter, 
  Loader2, 
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowUpRight,
  RefreshCw,
  Copy
} from 'lucide-react';

export default function PayoutsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPayouts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/payouts/?${params.toString()}`);
      setPayouts(response.data.payouts || []);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const getStatusConfig = (status) => {
    const configs = {
      created: { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock, label: 'Created' },
      pending_admin_review: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock, label: 'Pending Review' },
      approved: { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: CheckCircle, label: 'Approved' },
      processing: { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Loader2, label: 'Processing' },
      paid: { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle, label: 'Paid' },
      failed: { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'Failed' },
      rejected: { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'Rejected' },
      cancelled: { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: XCircle, label: 'Cancelled' },
      reversed: { color: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertCircle, label: 'Reversed' },
    };
    return configs[status] || configs.created;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (text) => {
    const ok = await copyText(text);
    ok ? toast.success('Copied to clipboard') : toast.error('Unable to copy. Please try again.');
  };

  const filteredPayouts = payouts.filter(payout => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        payout.order_id?.toLowerCase().includes(query) ||
        payout.beneficiary_name?.toLowerCase().includes(query) ||
        payout.utr?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending_admin_review', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'processing', label: 'Processing' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Payouts</h1>
          <p className="text-muted-foreground">Manage your payout requests</p>
        </div>
        <Button onClick={() => navigate('/dashboard/payouts/create')} data-testid="create-payout-btn">
          <Plus className="mr-2 h-4 w-4" />
          Create Payout
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by Order ID, Beneficiary, UTR..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchPayouts}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Payout History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredPayouts.length === 0 ? (
            <div className="text-center py-12">
              <Send className="w-12 h-12 mx-auto text-muted-foreground opacity-50 mb-4" />
              <p className="text-muted-foreground mb-4">No payouts found</p>
              <Button onClick={() => navigate('/dashboard/payouts/create')}>
                Create Your First Payout
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Order ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Beneficiary</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">UTR</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayouts.map((payout) => {
                    const statusConfig = getStatusConfig(payout.status);
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr 
                        key={payout.id} 
                        className="border-b last:border-0 hover:bg-muted/50"
                        data-testid={`payout-row-${payout.order_id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium">{payout.order_id}</span>
                            <button onClick={() => copyToClipboard(payout.order_id)}>
                              <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-sm">{payout.beneficiary_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {payout.bank_name} • {payout.account_number_masked}
                            </p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-semibold">{formatCurrency(payout.amount)}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={`${statusConfig.color} border`}>
                            <StatusIcon className={`w-3 h-3 mr-1 ${payout.status === 'processing' ? 'animate-spin' : ''}`} />
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {payout.utr && !payout.utr.startsWith('PROOF-') ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm">{payout.utr}</span>
                              <button onClick={() => copyToClipboard(payout.utr)}>
                                <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {formatDate(payout.created_at)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setShowDetails(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Details Dialog */}
      <MerchantPayoutModal payoutId={selectedPayout?.id} open={showDetails} onOpenChange={setShowDetails} />
    </div>
  );
}
